import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Server, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import madelmoldLogo from '@/assets/madelmold-logo.png';
import { z } from 'zod';
import {
  SERVER_PRESETS,
  getServerUrlOverride,
  setServerUrlOverride,
  tryEndpoint,
  autodetectServer,
  isCapacitor,
  getBaseUrl,
} from '@/lib/config';
import { toast } from 'sonner';

const loginSchema = z.object({
  email: z.string()
    .trim()
    .min(1, 'El email es requerido')
    .email('Email inválido')
    .max(255, 'El email no puede exceder 255 caracteres'),
  password: z.string()
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .max(128, 'La contraseña no puede exceder 128 caracteres'),
});

const Auth = () => {
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Server config state
  const [serverDialogOpen, setServerDialogOpen] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string>('');
  const [customUrl, setCustomUrl] = useState('');
  const [testingEndpoint, setTestingEndpoint] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [autodetecting, setAutodetecting] = useState(false);

  const currentBaseUrl = getBaseUrl() || '(web — ruta relativa)';
  const currentOverride = getServerUrlOverride();
  const currentPreset = SERVER_PRESETS.find((p) => p.url === currentOverride);
  const currentLabel = currentPreset?.label
    || (currentOverride ? `Personalizado: ${currentOverride}` : 'LAN planta (default)');

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Auto-detección al primer arranque (solo en Capacitor sin override previo)
  useEffect(() => {
    if (!isCapacitor) return;
    if (getServerUrlOverride()) return; // ya hay un override → no probar
    let cancelled = false;
    (async () => {
      setAutodetecting(true);
      const switched = await autodetectServer(3000);
      if (cancelled) return;
      setAutodetecting(false);
      if (switched) {
        toast.info(`Servidor detectado: ${getServerUrlOverride()}. Recargando...`);
        setTimeout(() => window.location.reload(), 800);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Inicializar dialog state al abrirlo
  const openServerDialog = () => {
    if (currentPreset) {
      setSelectedPresetId(currentPreset.id);
      setCustomUrl('');
    } else if (currentOverride) {
      setSelectedPresetId('custom');
      setCustomUrl(currentOverride);
    } else {
      setSelectedPresetId('lan');
      setCustomUrl('');
    }
    setTestResult(null);
    setServerDialogOpen(true);
  };

  const getSelectedUrl = (): string => {
    if (selectedPresetId === 'custom') return customUrl.trim();
    const p = SERVER_PRESETS.find((x) => x.id === selectedPresetId);
    return p?.url || '';
  };

  const handleTestConnection = async () => {
    const url = getSelectedUrl();
    if (!url) {
      setTestResult({ ok: false, msg: 'URL vacía' });
      return;
    }
    setTestingEndpoint(true);
    setTestResult(null);
    const ok = await tryEndpoint(url, 5000);
    setTestingEndpoint(false);
    setTestResult({
      ok,
      msg: ok ? `Conexión OK con ${url}` : `Sin respuesta de ${url}. ¿Estás en la red correcta?`,
    });
  };

  const handleSaveServer = () => {
    const url = getSelectedUrl();
    if (!url) {
      toast.error('URL vacía');
      return;
    }
    setServerUrlOverride(url);
    toast.success('Servidor guardado. Recargando app...');
    setTimeout(() => window.location.reload(), 600);
  };

  const handleResetServer = () => {
    setServerUrlOverride(null);
    toast.success('Servidor restaurado al default. Recargando...');
    setTimeout(() => window.location.reload(), 600);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      const validated = loginSchema.parse({
        email: loginEmail,
        password: loginPassword,
      });

      setLoading(true);
      await signIn(validated.email, validated.password);
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const fieldErrors: any = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0]] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else if (error?.message?.toLowerCase().includes('failed to fetch') || error?.name === 'TypeError') {
        toast.error('No se pudo conectar al servidor. Pulsa el icono ⚙ Servidor para cambiar el endpoint.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-magma p-4">
      <Card className="glass-card w-full max-w-md p-6 sm:p-8 relative">
        <Link
          to="/landing"
          className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Volver al inicio</span>
        </Link>

        <button
          type="button"
          onClick={openServerDialog}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-primary/10"
          title={`Servidor: ${currentLabel}`}
        >
          <Server className="w-4 h-4" />
          <span className="hidden sm:inline truncate max-w-[140px]">{currentPreset?.label || 'Servidor'}</span>
        </button>

        <div className="text-center mb-6 sm:mb-8 mt-8 sm:mt-0">
          <div className="flex justify-center mb-4">
            <img
              src={madelmoldLogo}
              alt="Madelmold"
              className="h-16 sm:h-20 w-auto object-contain"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent mb-2">
            Bienvenido a Athenea
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Sistema de Control de Producción
          </p>
          {autodetecting && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Detectando servidor...
            </p>
          )}
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-email" className="text-sm font-medium text-foreground">
              Correo electrónico
            </Label>
            <Input
              id="login-email"
              type="email"
              placeholder="tu@email.com"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              className="bg-white/90 border-primary/30 focus:border-primary focus:shadow-magma transition-all"
              required
            />
            {errors.email && (
              <p className="text-xs text-destructive bg-destructive/10 px-3 py-2 rounded-lg border border-destructive/30">
                {errors.email}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-password" className="text-sm font-medium text-foreground">
              Contraseña
            </Label>
            <Input
              id="login-password"
              type="password"
              placeholder="••••••••"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              className="bg-white/90 border-primary/30 focus:border-primary focus:shadow-magma transition-all"
              required
            />
            {errors.password && (
              <p className="text-xs text-destructive bg-destructive/10 px-3 py-2 rounded-lg border border-destructive/30">
                {errors.password}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-magma-light text-white hover:shadow-magma-hover transition-all font-semibold py-6 rounded-full mt-6"
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </Button>
        </form>

        <p className="text-[11px] text-muted-foreground text-center mt-4">
          Servidor: <span className="font-mono">{currentLabel}</span>
        </p>
      </Card>

      <Dialog open={serverDialogOpen} onOpenChange={setServerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" /> Configuración de servidor
            </DialogTitle>
            <DialogDescription>
              Selecciona el endpoint al que la app debe conectar. Si estás en planta, usa LAN. Fuera de planta, Tailscale.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {SERVER_PRESETS.map((p) => (
              <label
                key={p.id}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedPresetId === p.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                }`}
              >
                <input
                  type="radio"
                  name="server-preset"
                  value={p.id}
                  checked={selectedPresetId === p.id}
                  onChange={() => { setSelectedPresetId(p.id); setTestResult(null); }}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="font-medium text-sm">{p.label}</p>
                  <p className="text-xs text-muted-foreground font-mono">{p.url}</p>
                </div>
              </label>
            ))}

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedPresetId === 'custom' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
              }`}
            >
              <input
                type="radio"
                name="server-preset"
                value="custom"
                checked={selectedPresetId === 'custom'}
                onChange={() => { setSelectedPresetId('custom'); setTestResult(null); }}
                className="mt-1"
              />
              <div className="flex-1">
                <p className="font-medium text-sm mb-1">Personalizado</p>
                <Input
                  type="url"
                  placeholder="http://192.168.x.x:8080"
                  value={customUrl}
                  onChange={(e) => { setCustomUrl(e.target.value); setSelectedPresetId('custom'); setTestResult(null); }}
                  className="text-xs font-mono"
                  disabled={selectedPresetId !== 'custom'}
                />
              </div>
            </label>

            {testResult && (
              <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${testResult.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {testResult.ok ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                <span>{testResult.msg}</span>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="ghost" size="sm" onClick={handleResetServer}>
              Restaurar default
            </Button>
            <Button variant="outline" onClick={handleTestConnection} disabled={testingEndpoint}>
              {testingEndpoint ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Probar conexión
            </Button>
            <Button onClick={handleSaveServer} disabled={!getSelectedUrl()}>
              Guardar y reiniciar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
